import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DetectionResponseDto,
  DetectorTrainRequestDto,
  DetectorTrainResultDto,
  DetectorPreviewResponseDto,
} from '@signalvote/shared';
import { apiJson, apiResponse } from './apiFetch';
import { financeQueries, queryKeys } from '../../lib/queryKeys';

/**
 * PPR-003: pure detection mutation. POSTs a multipart file to
 * `/api/finance/detector/detect` and returns the candidate kind +
 * confirmation-banner summary. No DB writes.
 */
export function useDetectFile() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiJson<DetectionResponseDto>('/api/finance/detector/detect', {
        method: 'POST',
        body: form,
        // Do not set Content-Type — fetch + FormData handles the boundary.
        error: (error) => new Error(error.error || 'Failed to detect file type'),
      });
    },
  });
}

/**
 * PPR-003: user accepted the detector's guess. Opens an ImportBatch but
 * doesn't write a trained fingerprint (the detector already recognized it).
 */
export function useConfirmDetection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: DetectorTrainRequestDto) => {
      const response = await apiResponse('/api/finance/detector/confirm', {
        method: 'POST',
        json: request,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new DetectorErrorWithCode(
          body.error || 'Failed to confirm detection',
          body.code,
        );
      }
      return body as DetectorTrainResultDto;
    },
    onSuccess: () => {
      // financeQueries already matches the detector key, so a curated
      // detector invalidation alongside it is a provable no-op.
      queryClient.invalidateQueries(financeQueries);
    },
  });
}

/**
 * PPR-003: user picked a kind via the kind picker (after a detection miss or
 * a "no, this is something else" override). Trains a DetectorFingerprint and
 * opens an ImportBatch.
 */
export function useTrainDetector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: DetectorTrainRequestDto) => {
      const response = await apiResponse('/api/finance/detector/train', {
        method: 'POST',
        json: request,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new DetectorErrorWithCode(
          body.error || 'Failed to train detector',
          body.code,
        );
      }
      return body as DetectorTrainResultDto;
    },
    onSuccess: () => {
      // financeQueries already matches the detector key, so a curated
      // detector invalidation alongside it is a provable no-op.
      queryClient.invalidateQueries(financeQueries);
    },
  });
}

/**
 * ADR-015 D7: dry-run preview. Re-parses the uploaded bytes and returns the
 * detection summary (refund / duplicate / skipped counts + the aliases that
 * fired) plus the sampled row grid — the pre-commit review surface. Read-only:
 * the server writes nothing, so there is NOTHING to invalidate on success
 * (unlike confirm/train). Modeled as a mutation because the input is binary
 * bytes, not a stable cache key — there is no cross-campaign preview cache to go
 * stale; the panel resets its preview state on campaign switch instead.
 */
export function useDetectorPreview() {
  return useMutation({
    mutationFn: async (request: DetectorTrainRequestDto) => {
      const response = await apiResponse('/api/finance/detector/preview', {
        method: 'POST',
        json: request,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new DetectorErrorWithCode(
          body.error || 'Failed to preview file',
          body.code,
        );
      }
      return body as DetectorPreviewResponseDto;
    },
  });
}

/**
 * Backend error code (from `DetectorErrorCodes`) attached
 * to the thrown error so callers can switch on `code` instead of parsing
 * the message string. Mirrors the FIN-011 receipt-error pattern.
 */
export class DetectorErrorWithCode extends Error {
  public readonly code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Convert a `File` to its base64-encoded contents — the train/confirm
 * endpoints take bytes inline so the ImportBatch preserves them without a
 * follow-up multipart upload. Browser FileReader is the smallest path
 * compatible with Vitest's jsdom.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the `data:...;base64,` prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
